import {z} from "zod"

import {
  StrapiIdType,
  StrapiItemMeta,
  StrapiMediaOptional,
  StrapiMediasOptional,
} from "../../@reditor/core/strapi"
import {
  genDefaultBoolean,
  genDefaultString,
} from "../../@reditor/core/defaultTypes"

/** @typedef {z.infer<typeof ContentLink>} ContentLink */
export const ContentLink = z.object({
  url: z.string().nullish(),
  title: z.string().default("#"),
  hidden: z.boolean().optional().default(false),
  newTab: z.boolean().optional().default(false),
})

/** @typedef {z.infer<typeof ContentButton>} ContentButton */
export const ContentButton = ContentLink.extend({
  type: z.string().default("primary"),
})

/** @typedef {z.infer<typeof ContentDataJson>} ContentDataJson */
export const ContentDataJson = z.object({
  rating: z.number().nullish(),
  button: ContentButton.nullish(),
  buttons: z.array(ContentButton).nullish(),
  link: ContentLink.nullish(),
  links: z.array(ContentLink).nullish(),
  related_articles: z
    .object({
      hidden: z.boolean().default(false),
      links: z.array(
        z.object({
          url: z.string().nullish(),
          title: z.string().default("#"),
          hidden: z.boolean().optional().default(false),
          icon: z.string().nullish(),
        }),
      ),
    })
    .nullish(),
  value_list: z.array(z.object({
    text: z.string().nullish().default(''),
    image_url: z.string().nullish().default(''),
    sub_text: z.string().nullish().default('')
  })).nullish().optional()
})

/** @typedef {z.infer<typeof ContentAttributes>} ContentAttributes */
export const ContentAttributes = z.object({
  title: genDefaultString(""),
  description: genDefaultString(),
  url: z.string().nullable(),
  hidden: genDefaultBoolean(false),
  image: StrapiMediaOptional,
  images: StrapiMediasOptional,
  media: StrapiMediaOptional,
  name: z.string().nullable(),
  type: z.string().nullable(),
  richText: z.string().nullable(),
  data: ContentDataJson.nullish()
    .transform(v => v ?? {})
    .default({}),
})

/** @typedef {z.infer<typeof Content>} Content */
export const Content = z
  .object({
    id: StrapiIdType,
    attributes: ContentAttributes,
    meta: StrapiItemMeta.optional().nullable(),
  })
  .transform(({id, attributes, ...obj}) => ({
    ...obj,
    id,
    ...attributes,
  }))

/** @typedef {z.infer<typeof GetContentParams>} GetContentParams */
export const GetContentParams = StrapiIdType

/** @typedef {z.infer<typeof GetContentResult>} GetContentResult */
export const GetContentResult = z
  .object({
    data: Content,
    meta: z.object({}).optional(),
  })
  .transform(({data, ...obj}) => ({
    ...obj,
    ...data,
  }))
